import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  CardMedia
} from '@mui/material';
import { ChevronLeft, ChevronRight, Edit as EditIcon, Delete as DeleteIcon, InsertDriveFile as FileIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const CalendarView = ({ events, onEdit, onDelete }) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedEvent, setSelectedEvent] = useState(null);

  const startOfMonth = currentMonth.startOf('month');
  const endOfMonth = currentMonth.endOf('month');
  const startDate = startOfMonth.startOf('week');
  const endDate = endOfMonth.endOf('week');

  const days = [];
  let currentDate = startDate;
  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
    days.push(currentDate);
    currentDate = currentDate.add(1, 'day');
  }

  const getEventsForDate = (date) => {
    const eventsForDate = [];
    
    events.forEach(event => {
      if (!event.eventDate) return;
      
      const originalDate = dayjs(event.eventDate);
      
      // If it's an anniversary event, calculate anniversary dates
      if (event.isAnniversary) {
        const currentYear = date.year();
        const anniversaryDate = originalDate.year(currentYear);
        
        // Check if this date matches the anniversary date (same month and day)
        if (anniversaryDate.isSame(date, 'day')) {
          // Create a virtual event for this anniversary
          const yearsSince = currentYear - originalDate.year();
          eventsForDate.push({
            ...event,
            anniversaryDate: anniversaryDate.toDate(),
            yearsSince: yearsSince,
            isAnniversaryOccurrence: true
          });
        }
      } else {
        // Regular event - check if it matches the date
        if (originalDate.isSame(date, 'day')) {
          eventsForDate.push(event);
        }
      }
    });
    
    return eventsForDate;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const formatDate = (date) => {
    return dayjs(date).format('MMM DD, YYYY');
  };

  if (events.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No events found. Add your first event to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <IconButton onClick={handlePreviousMonth}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6">
            {currentMonth.format('MMMM YYYY')}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Typography key={day} variant="subtitle2" align="center" fontWeight="bold">
              {day}
            </Typography>
          ))}
          {days.map((date) => {
            const isCurrentMonth = date.isSame(currentMonth, 'month');
            const isToday = date.isSame(dayjs(), 'day');
            const dayEvents = getEventsForDate(date);

            return (
              <Paper
                key={date.format('YYYY-MM-DD')}
                elevation={isToday ? 3 : 0}
                sx={{
                  minHeight: 100,
                  p: 1,
                  bgcolor: isCurrentMonth ? 'background.paper' : 'action.hover',
                  border: isToday ? 2 : 0,
                  borderColor: isToday ? 'primary.main' : 'transparent',
                  cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => dayEvents.length > 0 && setSelectedEvent(dayEvents[0])}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isToday ? 'bold' : 'normal',
                    color: isCurrentMonth ? 'text.primary' : 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {date.format('D')}
                </Typography>
                {dayEvents.slice(0, 2).map((event, index) => (
                  <Chip
                    key={`${event.id}-${event.isAnniversaryOccurrence ? event.anniversaryDate : event.eventDate}-${index}`}
                    label={event.title}
                    size="small"
                    color={event.category === 'work' ? 'primary' : 'secondary'}
                    sx={{
                      width: '100%',
                      mb: 0.5,
                      fontSize: '0.7rem',
                      height: 20
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                    }}
                  />
                ))}
                {dayEvents.length > 2 && (
                  <Typography variant="caption" color="text.secondary">
                    +{dayEvents.length - 2} more
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Paper>

      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>{selectedEvent.title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedEvent.isAnniversaryOccurrence 
                  ? formatDate(selectedEvent.anniversaryDate) 
                  : formatDate(selectedEvent.eventDate)}
                {selectedEvent.isAnniversaryOccurrence && selectedEvent.yearsSince > 0 && (
                  <Chip 
                    label={`${selectedEvent.yearsSince} ${selectedEvent.yearsSince === 1 ? 'year' : 'years'}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Chip
                label={selectedEvent.category || 'N/A'}
                size="small"
                color={selectedEvent.category === 'work' ? 'primary' : 'secondary'}
                sx={{ mb: 2 }}
              />
              {selectedEvent.location && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  📍 {selectedEvent.location}
                </Typography>
              )}
              {selectedEvent.description && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {selectedEvent.description}
                </Typography>
              )}
              {selectedEvent.tags && selectedEvent.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 2 }}>
                  {selectedEvent.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
              {(selectedEvent.documentUrl || selectedEvent.imageUrl) && (
                <Box sx={{ mt: 2 }}>
                  {selectedEvent.documentType === 'pdf' || (!selectedEvent.documentType && (selectedEvent.documentUrl || selectedEvent.imageUrl)?.endsWith('.pdf')) ? (
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                      <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {selectedEvent.fileName || 'PDF Document'}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenIcon />}
                        href={selectedEvent.documentUrl || selectedEvent.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open PDF
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <CardMedia
                        component="img"
                        height="200"
                        image={selectedEvent.documentUrl || selectedEvent.imageUrl}
                        alt={selectedEvent.title}
                        sx={{ objectFit: 'cover', borderRadius: 1, mb: 1 }}
                      />
                      <Link
                        href={selectedEvent.documentUrl || selectedEvent.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                      >
                        View Full Size
                      </Link>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => onEdit(selectedEvent)} startIcon={<EditIcon />}>
                Edit
              </Button>
              <Button
                onClick={() => {
                  onDelete(selectedEvent.id);
                  setSelectedEvent(null);
                }}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
              <Button onClick={() => setSelectedEvent(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CalendarView;

